import { useState, useEffect, useRef } from 'react'

interface SwipeNavigationProps {
  totalTopics: number
  onTopicChange?: (index: number) => void
}

export function useSwipeNavigation({
  totalTopics,
  onTopicChange,
}: SwipeNavigationProps) {
  const [currentTopic, setCurrentTopic] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef<number>(0)
  const touchEndX = useRef<number>(0)

  const goToTopic = (index: number) => {
    if (index >= 0 && index < totalTopics) {
      setCurrentTopic(index)
      onTopicChange?.(index)

      if (containerRef.current) {
        const container = containerRef.current
        const topicWidth = container.offsetWidth
        container.scrollTo({
          left: topicWidth * index,
          behavior: 'smooth',
        })
      }
    }
  }

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    const swipeThreshold = 50
    const diff = touchStartX.current - touchEndX.current

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && currentTopic < totalTopics - 1) {
        // Swipe left - next topic
        goToTopic(currentTopic + 1)
      } else if (diff < 0 && currentTopic > 0) {
        // Swipe right - previous topic
        goToTopic(currentTopic - 1)
      }
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('touchstart', handleTouchStart)
    container.addEventListener('touchmove', handleTouchMove)
    container.addEventListener('touchend', handleTouchEnd)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [currentTopic, totalTopics])

  return {
    currentTopic,
    goToTopic,
    containerRef,
  }
}
